import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import gatheringService from '../services/gatheringService';
import { useDialog } from '../context';
import useIsMobile from '../hooks/useIsMobile';
import { PillButton, TagPill } from './club/clubUi';
import { kicker, RED_TINT } from './gathering/gatheringUi';
import { gatheringStatus, STATUS_PILL } from './gathering/gatheringModel';
import { posterPlaceholder } from '../utils/posterPlaceholder';
import { money, plural, dateLabel, whenLabel } from '../utils/surfaceFormat';

/**
 * GatheringsManager — a club's gatherings, one white card each (design_handoff_gatherings §4):
 * thumbnail, status pill, dates, name, the EVENTS · IN · TICKETS strip, an action row
 * (AGENDA · PUBLISH · EDIT · MANAGE · OPEN · DELETE), an inline delete confirm and an agenda
 * drawer that attaches / detaches club events. Mounted by `GatheringsManagePage`
 * (`/clubs/:id/gatherings`). `attachable` is keyed by gathering so opening a second drawer
 * never flashes the first one's list.
 */
const MUTED = '#6B7280';
const SUBTLE = '#9CA3AF';
const BODY = '#374151';
const RULE = '2px solid #F2F2F2';

export default function GatheringsManager({ clubId, clubName }) {
  const navigate = useNavigate();
  const { showAlert } = useDialog();
  const mobile = useIsMobile();
  const [gatherings, setGatherings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [attachable, setAttachable] = useState({}); // gatheringId → rows
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await gatheringService.listForClub(clubId);
    if (res.success) setGatherings(res.data || []);
    setLoading(false);
  }, [clubId]);

  useEffect(() => { load(); }, [load]);

  const togglePublish = async (g) => {
    const next = g.status === 'published' ? 'draft' : 'published';
    const res = await gatheringService.update(g.id, { status: next });
    if (res.success) load();
    else await showAlert({ title: 'COULD NOT UPDATE', message: res.error?.message || 'Something went wrong.', type: 'error' });
  };

  const removeGathering = async (g) => {
    setConfirmId(null);
    const res = await gatheringService.delete(g.id);
    if (!res.success) { await showAlert({ title: 'COULD NOT DELETE', message: res.error?.message || 'Something went wrong.', type: 'error' }); return; }
    if (expanded === g.id) setExpanded(null);
    load();
  };

  const loadAttachable = async (gid) => {
    const res = await gatheringService.listAttachableEvents(gid);
    if (res.success) setAttachable((a) => ({ ...a, [gid]: res.data || [] }));
  };
  const openAgenda = async (g) => {
    if (expanded === g.id) { setExpanded(null); return; }
    setExpanded(g.id);
    await loadAttachable(g.id);
  };

  const toggleAttach = async (g, ev) => {
    const res = ev.inThisGathering
      ? await gatheringService.detachEvent(g.id, ev.id)
      : await gatheringService.attachEvent(g.id, ev.id);
    if (res.success) {
      await loadAttachable(g.id);
      load();
    } else {
      await showAlert({ title: 'COULD NOT UPDATE', message: res.error?.message || 'Something went wrong.', type: 'error' });
    }
  };

  const panelPad = mobile ? '18px 16px' : '22px 24px';
  const act = (label, onClick, dark = false, testId) => (
    <PillButton key={label} variant={dark ? 'primary' : 'tertiary'} size={36} fontSize={10} tracking=".12em" onClick={onClick} data-testid={testId}>{label}</PillButton>
  );

  return (
    <div className="flex flex-col font-mono" style={{ gap: mobile ? 10 : 16 }} data-testid="gatherings-manager">
      <span className="flex flex-wrap items-end justify-between" style={{ gap: 12, padding: mobile ? '16px 16px 0' : '4px 0 0' }}>
        <span className="flex flex-col" style={{ gap: 8 }}>
          <button type="button" onClick={() => navigate(`/clubs/${clubId}/manage`)} className="text-left" style={{ ...kicker(10), border: 'none', background: 'none', padding: 0, fontFamily: 'inherit', cursor: 'pointer' }}>← MANAGE CLUB{clubName ? ` · ${clubName}` : ''}</button>
          <h1 style={{ margin: 0, textTransform: 'none', fontSize: mobile ? 26 : 36, fontWeight: 700, lineHeight: 1.1 }}>Gatherings</h1>
          <span style={{ fontSize: 12, lineHeight: 1.6, color: BODY, maxWidth: 560 }}>A gathering groups this club's events by day: a release weekend, a retreat, a series.</span>
        </span>
        <PillButton variant="cyan" size={46} className="hover:bg-black hover:text-[#00FFFF]" onClick={() => navigate(`/clubs/${clubId}/gatherings/new`)} data-testid="new-gathering">+ NEW GATHERING</PillButton>
      </span>

      {loading ? (
        <div className="bg-white" style={{ padding: '28px 22px', ...kicker(11), color: SUBTLE }}>LOADING…</div>
      ) : gatherings.length === 0 ? (
        <div className="flex flex-col bg-white" style={{ gap: 6, padding: '28px 22px' }} data-testid="manager-empty">
          <span style={kicker(12)}>NO GATHERINGS YET.</span>
          <span style={{ fontSize: 12, color: MUTED }}>Spin up a multi-day agenda of this club's events.</span>
        </div>
      ) : gatherings.map((g) => {
        const status = gatheringStatus(g);
        const pill = STATUS_PILL[status] || STATUS_PILL.draft;
        const open = expanded === g.id;
        const rows = attachable[g.id];
        const onCount = (rows || []).filter((e) => e.inThisGathering).length;
        const thumb = mobile ? 64 : 88;
        return (
          <div key={g.id} className="flex flex-col bg-white" data-testid={`manager-${g.id}`}>
            <div className="flex flex-wrap items-center" style={{ gap: '14px 20px', padding: panelPad }}>
              <span className="flex-shrink-0 overflow-hidden" style={{ width: thumb, height: thumb, background: g.posterUrl ? '#F2F2F2' : posterPlaceholder(g.id) }}>
                {g.posterUrl && <img src={g.posterUrl} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
              </span>
              <span className="flex min-w-0 flex-col" style={{ flex: '1 1 220px', gap: 6 }}>
                <span className="flex flex-wrap items-center" style={{ gap: 8 }}>
                  <TagPill bg={pill.bg} fg={pill.fg} ring={pill.ring} size={9} padding="4px 10px">{pill.label}</TagPill>
                  <span style={{ ...kicker(10, '.12em'), color: MUTED }}>{g.startsAt ? `${dateLabel(g.startsAt)}${g.endsAt && dateLabel(g.endsAt) !== dateLabel(g.startsAt) ? ` – ${dateLabel(g.endsAt)}` : ''}` : ''}</span>
                </span>
                <button type="button" onClick={() => navigate(`/clubs/${clubId}/gatherings/${g.id}`)} className="text-left hover:underline" style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.2, border: 'none', background: 'none', padding: 0, fontFamily: 'inherit', cursor: 'pointer', overflowWrap: 'anywhere' }}>{g.name}</button>
                <span style={{ fontSize: 11, color: MUTED }}>{plural(g.eventCount || 0, 'event').toLowerCase()} on the agenda</span>
              </span>
              <span className="grid" style={{ gridTemplateColumns: 'repeat(3,auto)', gap: 2, background: '#F2F2F2' }} data-testid={`stats-${g.id}`}>
                {[[String(g.eventCount || 0), 'EVENTS'], [g.memberCount != null ? String(g.memberCount) : '—', 'IN'], [g.ticketTotalCents != null ? money(g.ticketTotalCents) : '—', 'TICKETS']].map(([n, k]) => (
                  <span key={k} className="flex flex-col items-center bg-white" style={{ gap: 3, padding: '10px 14px' }}>
                    <span style={{ fontSize: 15, fontWeight: 800 }}>{n}</span>
                    <span style={{ ...kicker(8, '.14em'), color: MUTED }}>{k}</span>
                  </span>
                ))}
              </span>
            </div>
            <div className="flex flex-wrap" style={{ gap: 6, padding: mobile ? '10px 16px' : '12px 24px', borderTop: RULE }}>
              {act(open ? 'HIDE AGENDA' : 'AGENDA', () => openAgenda(g), open, `agenda-${g.id}`)}
              {status !== 'ended' && act(g.status === 'published' ? 'UNPUBLISH' : 'PUBLISH', () => togglePublish(g), false, `publish-${g.id}`)}
              {act('EDIT', () => navigate(`/clubs/${clubId}/gatherings/${g.id}/edit`))}
              {act('MANAGE', () => navigate(`/clubs/${clubId}/gatherings/${g.id}/manage`), false, `manage-${g.id}`)}
              {act('OPEN', () => navigate(`/clubs/${clubId}/gatherings/${g.id}`), true)}
              <span style={{ flex: 1 }} />
              <button type="button" onClick={() => setConfirmId(g.id)} className="font-mono" data-testid={`delete-${g.id}`} style={{ height: 36, padding: '0 14px', border: 'none', borderRadius: 999, background: 'transparent', color: '#DC2626', fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '.12em', cursor: 'pointer' }}>DELETE</button>
            </div>
            {confirmId === g.id && (
              <div className="flex flex-wrap items-center" style={{ gap: '10px 14px', padding: panelPad, background: RED_TINT }} data-testid={`confirm-${g.id}`}>
                <span style={{ flex: '1 1 260px', fontSize: 12, lineHeight: 1.55 }}><span style={{ fontWeight: 700 }}>Delete {g.name}?</span> Its events stay. They become standalone club events again.</span>
                <PillButton variant="ghost" size={38} fontSize={10} onClick={() => setConfirmId(null)}>KEEP IT</PillButton>
                <PillButton variant="armed" size={38} fontSize={10} onClick={() => removeGathering(g)} data-testid={`confirm-delete-${g.id}`}>DELETE</PillButton>
              </div>
            )}
            {open && (
              <div className="flex flex-col" style={{ gap: 4, padding: panelPad, background: '#FAFAFA', borderTop: RULE }} data-testid={`drawer-${g.id}`}>
                <span className="flex justify-between" style={{ gap: 8, paddingBottom: 6 }}>
                  <span style={kicker(10)}>CLUB EVENTS · ON THE AGENDA OR NOT</span>
                  <span style={{ ...kicker(9, '.14em'), color: SUBTLE }}>{onCount} ON</span>
                </span>
                {!rows ? <span style={{ fontSize: 12, color: MUTED }}>Loading…</span>
                  : rows.length === 0 ? <span style={{ fontSize: 12, color: MUTED }}>No club events yet. Create events under this club first.</span>
                    : rows.map((ev) => {
                      const other = !ev.inThisGathering && !!ev.gatheringId;
                      return (
                        <div key={ev.id} className="flex flex-wrap items-center" style={{ gap: '6px 14px', padding: '10px 14px', borderRadius: 16, background: ev.inThisGathering ? '#fff' : 'transparent', opacity: other ? 0.45 : 1 }}>
                          <span className="flex min-w-0 flex-col" style={{ flex: '1 1 200px', gap: 2 }}>
                            <span style={{ fontSize: 13, fontWeight: 700 }}>{ev.title}</span>
                            <span style={{ fontSize: 10, color: MUTED }}>{whenLabel(ev.dateTime)}</span>
                          </span>
                          <PillButton variant={ev.inThisGathering ? 'tertiary' : 'primary'} size={32} fontSize={9} disabled={other} onClick={() => !other && toggleAttach(g, ev)} style={other ? { cursor: 'default' } : undefined}>
                            {ev.inThisGathering ? 'REMOVE' : other ? 'IN ANOTHER' : '+ ADD'}
                          </PillButton>
                        </div>
                      );
                    })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
